<template>
  <div>
    <loading v-if="isLoading"></loading>

    <template v-else>
      <div class="rooms border-right">
        <header
          class="d-flex justify-content-between align-items-center sticky-top"
        >
          <span class="text-left d-sm-none"></span>
          <h1 class="px-3">訊息中心</h1>
          <span class="text-right"> </span>
        </header>

        <div class="px-3 pt-3 pb-2">
          <div class="d-flex align-items-center mb-2">
            <div class="search flex-grow-1">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path
                  d="M500.3 443.7l-119.7-119.7c27.22-40.41 40.65-90.9 33.46-144.7C401.8 87.79 326.8 13.32 235.2 1.723C99.01-15.51-15.51 99.01 1.724 235.2c11.6 91.64 86.08 166.7 177.6 178.9c53.8 7.189 104.3-6.236 144.7-33.46l119.7 119.7c15.62 15.62 40.95 15.62 56.57 0C515.9 484.7 515.9 459.3 500.3 443.7zM79.1 208c0-70.58 57.42-128 128-128s128 57.42 128 128c0 70.58-57.42 128-128 128S79.1 278.6 79.1 208z"
                />
              </svg>
              <b-form-input
                type="search"
                v-model.trim="keyword"
                placeholder="姓名搜尋"
                @input="keywordSearchDebounced"
              ></b-form-input>
            </div>

            <div v-if="allowFilterStatus" class="status-filter ml-2">
              <ChatroomMultiselect
                title="處理狀態"
                :filters="statusOptions"
                :showKeywordFilter="false"
                :selectedOptions="selectedStatus"
                @click="handleSelectedStatus"
              />
            </div>
          </div>

          <!-- 已選的篩選狀態 chips，顯示目前篩選的狀態，點擊可移除 -->
          <div
            v-if="allowFilterStatus && selectedStatus.length"
            class="d-flex flex-wrap align-items-center mb-2"
          >
            <b-badge
              v-for="s in selectedStatus"
              :key="s.value"
              :variant="s.variant"
              class="mr-1 mb-1"
              role="button"
              @click="handleSelectedStatus(s)"
            >
              {{ s.text }} ✕
            </b-badge>
          </div>
        </div>

        <b-list-group>
          <template v-if="filteredChatrooms.length > 0">
            <b-list-group-item
              v-for="i in filteredChatrooms"
              :key="i.id"
              class="d-flex align-items-center position-relative"
              role="button"
              :variant="chatroomId == i.id ? 'secondary' : 'default'"
              @click="handleLink(i.id)"
            >
              <span class="pinned" v-if="isPinned(i)">
                <i class="fa fa-thumb-tack text-warning" aria-hidden="true"></i>
              </span>
              <template>
                <b-avatar
                  class="mr-3"
                  :src="getChatroomAvatar(i)"
                ></b-avatar>
                <div class="mr-auto font-weight-bold">
                  {{ resolveTitle(i) }}
                  <div class="text-muted small">
                    {{ getChatroomLastMessage(i) }}
                  </div>
                </div>
              </template>

              <b-badge
                v-if="i.is_new_created"
                pill
                variant="outline-info"
              >
                New
              </b-badge>
              <b-badge v-if="i.unread > 0" pill variant="primary">{{
                i.unread
              }}</b-badge>
              <span
                v-if="!i.is_new_created && !(i.unread > 0)"
                class="text-muted small"
              >
                {{ formatDate(i.updated_at) }}
              </span>
            </b-list-group-item>
          </template>
          <b-list-group-item v-else class="empty">
            <img src="./images/empty.svg" />
            <div class="mt-3 text-dark small">目前沒有名單</div>
          </b-list-group-item>
        </b-list-group>
        <Pagination v-model="page" :total-page="meta.lastPage" />
      </div>
      <div v-if="chatroomId" class="flex-grow-1 d-none d-sm-block">
        <Chatbox :chatroomId="chatroomId" :key="chatroomId" />
      </div>
      <div
        v-else
        class="
          empty-chat
          d-none d-sm-flex
          justify-content-center
          align-items-center
        "
      >
        <div class="text-center">
          <img src="./images/empty_chat.svg" />
          <div class="mt-3 text-dark small">選一位客戶開始一段對話吧</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import _ from "lodash";
import format from "date-fns/format";
import { mapActions } from "vuex";
import echo from "@/utils/liff/laravelEcho";
import withFetcher from "@/mixins/liff/withFetcher";
import Chatbox from "@/pages/Liff/Chatroom/Chatbox";
import { setTitle } from "@/store/modules/liff/helpers";
import { getChatroomAvatar, getChatroomTitle, formatDate, getChatroomLastMessage } from "@/utils/chatroomHelper.js";
import { resolveChatroomTitle } from "@/utils/liffChatroomTitle";
import Pagination from "@/components/Page/Liff/Shared/Pagination";
import ChatroomMultiselect from "@/components/Page/Chatroom/ChatroomMultiselect.vue";

export default {
  mixins: [withFetcher],
  components: { Chatbox, Pagination, ChatroomMultiselect },
  data: () => ({
    chatroomId: null,
    chatrooms: [],
    channel: null,
    page: 1,
    meta: {
      total: 0,
      lastPage: 1,
    },
    keyword: "",
    isLoading: true,
    liffChatroomConfig: null,
    selectedStatus: [],
    statusOptions: [
      { text: "待處理", value: "pending", variant: "danger" },
      { text: "處理中", value: "doing", variant: "warning" },
      { text: "處理完畢", value: "done", variant: "primary" },
      { text: "未設定", value: "not-setting", variant: "secondary" },
    ],
  }),
  computed: {
    asStaff() {
      return this.$store.state.liffChatroom.asStaff;
    },
    allowFilterStatus() {
      // 僅 staff 身份可使用 status filter，後台功能搬入 LIFF 給 staff 使用
      if (!this.asStaff) return false;
      return !!(
        this.liffChatroomConfig &&
        this.liffChatroomConfig.sender_type_staff &&
        this.liffChatroomConfig.sender_type_staff.chatroom_list &&
        this.liffChatroomConfig.sender_type_staff.chatroom_list.show_status_filter
      );
    },
    selectedStatusValue() {
      return this.selectedStatus.map((s) => s.value).join(",");
    },
    // staff 走 server-side filter，customer 維持 client-side（依姓名 match）
    filteredChatrooms() {
      let list;
      if (this.asStaff) {
        list = this.chatrooms;
      } else {
        const kw = (this.keyword || "").toLowerCase();
        list = kw
          ? this.chatrooms.filter((c) =>
              c.customers.find((x) => x.name && x.name.toLowerCase().indexOf(kw) >= 0)
            )
          : this.chatrooms;
      }
      // 已釘選置頂（穩定排序，其餘維持後端原序）
      return [...list].sort(
        (a, b) => (this.isPinned(b) ? 1 : 0) - (this.isPinned(a) ? 1 : 0)
      );
    },
  },
  methods: {
    format,
    ...mapActions({
      getChatrooms: "liffChatroom/getChatrooms",
      getChatroom: "liffChatroom/getChatroom",
    }),
    async fetch() {
      const { data, meta } = await this.getChatrooms({
        page: this.page,
        status: this.asStaff ? (this.selectedStatusValue || undefined) : undefined,
        keyword: this.asStaff ? (this.keyword || undefined) : undefined,
      });
      this.chatrooms = data.map((chatroom) => {
        if (!chatroom.message) {
          chatroom.is_new_created = true;
        }
        return chatroom;
      });
      this.meta = {
        total: meta.total,
        lastPage: meta.lastPage,
      };
      try {
        await this.$store.dispatch('liffModule/fetchModule', 'liff_chatroom');
        const module = this.$store.getters['liffModule/getModule']('liff_chatroom');
        this.liffChatroomConfig = (module && module.module_config) || null;
      } catch (e) {
        this.liffChatroomConfig = null;
      }
      setTitle(this.$store.state.liffChatroom.liffInfo.liff_og_title);
      this.isLoading = false;
      this.connectToChannel();
    },
    async fetchChatroomsByPage(page) {
      this.isLoading = true;
      const { data, meta } = await this.getChatrooms({
        page,
        status: this.asStaff ? (this.selectedStatusValue || undefined) : undefined,
        keyword: this.asStaff ? (this.keyword || undefined) : undefined,
      });
      this.chatrooms = data.map((chatroom) => {
        if (!chatroom.message) {
          chatroom.is_new_created = true;
        }
        return chatroom;
      });
      this.meta = {
        total: meta.total,
        lastPage: meta.lastPage,
      };
      this.isLoading = false;
    },
    async fetchWithFilter() {
      this.isLoading = true;
      const { data, meta } = await this.getChatrooms({
        page: this.page,
        status: this.asStaff ? (this.selectedStatusValue || undefined) : undefined,
        keyword: this.asStaff ? (this.keyword || undefined) : undefined,
      });
      this.chatrooms = data.map((chatroom) => {
        if (!chatroom.message) {
          chatroom.is_new_created = true;
        }
        return chatroom;
      });
      this.meta = {
        total: meta.total,
        lastPage: meta.lastPage,
      };
      this.isLoading = false;
    },
    async handleSelectedStatus(option) {
      const idx = this.selectedStatus.findIndex((x) => x.value === option.value);
      if (idx >= 0) {
        this.selectedStatus.splice(idx, 1);
      } else {
        this.selectedStatus.push(option);
      }
      this.page = 1;
      await this.fetchWithFilter();
    },
    keywordSearchDebounced() {},
    handleLink(id) {
      if (window.innerWidth <= 576) {
        this.$router.push({
          name: "LiffChatroomShow",
          params: { id: id },
          query: { ...this.$route.query, back: '1' },
        });
      } else {
        this.chatroomId = id;
      }
    },
    getTarget(customers) {
      var _this = this;
      return customers.find((x) => {
        return x.outer_code != _this.$store.state.liffChatroom.user.outer_code;
      });
    },

    getAvatar(customer) {
      if (customer.avatar_url !== undefined && customer.avatar_url)
        return customer.avatar_url;
      else if (customer.data.pictureUrl != undefined)
        return customer.data.pictureUrl;
      else return null;
    },
    getDisplayName(customer) {
      if (customer.name !== undefined && customer.name) return customer.name;
      else if (customer.data.displayName != undefined)
        return customer.data.displayName;
      else return null;
    },

    getChatroomTitle,
    getChatroomAvatar,
    formatDate,
    getChatroomLastMessage,

    isPinned(chatroom) {
      return !!(
        chatroom &&
        chatroom.extensions &&
        chatroom.extensions[0] &&
        chatroom.extensions[0].pinned_at
      );
    },

    resolveTitle(chatroom) {
      const asStaff = this.$store.state.liffChatroom.asStaff;
      const rendered = resolveChatroomTitle(
        chatroom,
        asStaff,
        this.liffChatroomConfig,
        'chatroom_list'
      );
      return rendered || getChatroomTitle(chatroom);
    },

    // WebSocket 連線
    connectToChannel() {
      const user = this.$store.state.liffChatroom.user;
      const asStaff = this.$store.state.liffChatroom.asStaff;

      if (asStaff && user.staff) {
        this.channel = echo.private(`staff.${user.staff.id}`);
      } else {
        this.channel = echo.private(`customer.${user.id}`);
      }

      this.channel.listen('.chatroom.created', this.handleChatroomCreated);
    },
    leaveChannel() {
      if (this.channel) {
        echo.leave(this.channel.name);
        this.channel = null;
      }
    },
    async handleChatroomCreated(event) {
      const { chatroom } = event;
      // 檢查是否已存在
      if (this.chatrooms.find(c => c.id === chatroom.id)) {
        return;
      }
      try {
        const chatroomData = await this.getChatroom({ chatroomId: chatroom.id });
        chatroomData.is_new_created = true;
        this.chatrooms.unshift(chatroomData);
        this.meta.total += 1;
        this.meta.lastPage = Math.ceil(this.meta.total / 20);
      } catch (e) {
        console.error('handleChatroomCreated error:', e);
      }
    },
  },
  watch: {
    page(newVal) {
      this.fetchChatroomsByPage(newVal);
    },
  },
  created() {
    if (this.$route.query?.page) {
      this.page = Number(this.$route.query.page);
    }
    // 只有 staff 才走 server-side debounced fetch；customer 維持 client-side filter（透過 filteredChatrooms 即時反映）
    this.keywordSearchDebounced = _.debounce(async () => {
      if (!this.asStaff) return;
      this.page = 1;
      await this.fetchWithFilter();
    }, 1000);
  },
  beforeDestroy() {
    this.leaveChannel();
  },
};
</script>

<!-- ChatroomMultiselect 用 font-awesome 顯示 checkbox icon，需要全域載入 -->
<style lang="scss">
@import "../../../../node_modules/font-awesome/css/font-awesome.min.css";
</style>

<style lang="scss" scoped>
::v-deep {
  --s-primary: #1e88e5;
  --s-gray-dark: #2c2c2e;
  --s-gray-light: #c0c4cc;
  --s-white: #ffffff;

  .s-pagination {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin: 32px auto 50px;
    list-style: none;
    padding: 0;

    .s-pagination__center {
      line-height: 40px;
      display: flex;
      align-items: center;

      span {
        font-size: 30px;
        line-height: 36px;
        text-align: center;
        color: #e5e5ea;
        font-family: "SF UI Text";
        font-style: normal;
        font-weight: 400;
        margin-right: 10px;
        margin-left: 10px;
      }
    }

    li:not(:last-child) {
      margin-right: 12px;
    }

    li {
      list-style: none;
    }

    li a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 0.125rem;
      color: var(--s-gray-dark);
      border: 1px solid var(--s-primary);
      background-color: var(--s-white);
      text-decoration: none;
    }

    li[disabled] a {
      pointer-events: none;
      border: 1px solid var(--s-gray-light);
    }
  }
}

.pinned {
  position: absolute;
  top: 5px;
  left: 10px;
  transform: rotate(45deg);
  z-index: 1;
}
.search {
  position: relative;
  input {
    padding-left: 45px;
  }
  svg {
    position: absolute;
    left: 18px;
    top: 14px;
    height: 15px;
  }
}
.status-filter {
  flex: 0 0 130px;
  min-width: 0;
}
.rooms {
  min-width: 375px;
  width: 375px;
  height: 100vh;
  overflow-y: auto;
}

header {
  background: linear-gradient(90deg, #1e88e5 0%, #2196f3 100%), #1976d2;
  color: #fff;
  h1 {
    font-size: 24px;
  }
}

.empty {
  padding: 20% 35%;
  text-align: center;
}

.empty-chat {
  width: 100%;
  height: 100vh;
}

@media only screen and (max-width: 576px) {
  .rooms {
    width: 100%;
    min-width: none;
  }

  header {
    background: linear-gradient(90deg, #1e88e5 0%, #2196f3 100%), #1976d2;
    color: #fff;
    h1 {
      font-size: 16px;
    }
  }
}
</style>
